/**
 * PropertyForm — Shared form fields for create and edit property modes.
 * Extracted from CreatePropertyModal + PropertyDetailModal to eliminate duplication.
 */
import {
  Home,
  MapPin,
  Maximize,
  Banknote,
  BedDouble,
  Bath,
  DoorOpen,
  Image as ImageIcon,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ProofreadingIndicator } from '@/components/properties/ProofreadingIndicator';
import type { UseProofreaderReturn } from '@/hooks/useProofreader';

// ═══════════════════════════════════════════
// Shared types and constants
// ═══════════════════════════════════════════

export interface PropertyFormData {
  address: string;
  postal_code: string;
  city: string;
  property_type: string;
  size_sqm: string;
  rooms: string;
  bedrooms: string;
  bathrooms: string;
  price: string;
  description: string;
  status: string;
  images: string[];
}

export const initialFormData: PropertyFormData = {
  address: '',
  postal_code: '',
  city: '',
  property_type: 'íbúð',
  size_sqm: '',
  rooms: '',
  bedrooms: '',
  bathrooms: '',
  price: '',
  description: '',
  status: 'á_markaði',
  images: [],
};

export const propertyTypeOptions = [
  { value: 'íbúð', label: 'Íbúð' },
  { value: 'einbýli', label: 'Einbýlishús' },
  { value: 'raðhús', label: 'Raðhús' },
  { value: 'parhús', label: 'Parhús' },
  { value: 'sumarhús', label: 'Sumarhús' },
  { value: 'atvinnuhúsnæði', label: 'Atvinnuhúsnæði' },
];

export const statusOptions = [
  { value: 'á_markaði', label: 'Á markaði' },
  { value: 'selt', label: 'Selt' },
  { value: 'frátekið', label: 'Frátekið' },
  { value: 'afskráð', label: 'Afskráð' },
];

/** Convert form strings → API-ready partial property data */
export function toPropertyData(formData: PropertyFormData) {
  return {
    address: formData.address.trim(),
    postal_code: formData.postal_code.trim() || undefined,
    city: formData.city.trim() || undefined,
    property_type: formData.property_type,
    size_sqm: formData.size_sqm ? parseInt(formData.size_sqm, 10) : undefined,
    rooms: formData.rooms ? parseInt(formData.rooms, 10) : undefined,
    bedrooms: formData.bedrooms ? parseInt(formData.bedrooms, 10) : undefined,
    bathrooms: formData.bathrooms ? parseInt(formData.bathrooms, 10) : undefined,
    price: formData.price ? parseInt(formData.price.replace(/\D/g, ''), 10) : undefined,
    description: formData.description.trim() || undefined,
    status: formData.status,
  };
}

// ═══════════════════════════════════════════
// PropertyForm component
// ═══════════════════════════════════════════

interface PropertyFormProps {
  formData: PropertyFormData;
  onChange: (field: keyof PropertyFormData, value: string | string[]) => void;
  proofreader: UseProofreaderReturn;
  /** Extra content after description (e.g. image gallery, AI tools) */
  children?: React.ReactNode;
}

export function PropertyForm({ formData, onChange, proofreader, children }: PropertyFormProps) {
  return (
    <div className="space-y-6">
      {/* Address section */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-400" />
          Staðsetning
        </h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label htmlFor="pf-address">Heimilisfang *</Label>
            <Input
              id="pf-address"
              value={formData.address}
              onChange={(e) => onChange('address', e.target.value)}
              placeholder="Laugavegur 77"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pf-postal_code">Póstnúmer</Label>
              <Input
                id="pf-postal_code"
                value={formData.postal_code}
                onChange={(e) => onChange('postal_code', e.target.value)}
                placeholder="101"
              />
            </div>
            <div>
              <Label htmlFor="pf-city">Borg</Label>
              <Input
                id="pf-city"
                value={formData.city}
                onChange={(e) => onChange('city', e.target.value)}
                placeholder="Reykjavík"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Property details section */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <Home className="w-4 h-4 text-gray-400" />
          Upplýsingar um eign
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="pf-property_type">Tegund eignar</Label>
            <Select
              value={formData.property_type}
              onValueChange={(value) => onChange('property_type', value)}
            >
              <SelectTrigger id="pf-property_type">
                <SelectValue placeholder="Veldu tegund" />
              </SelectTrigger>
              <SelectContent>
                {propertyTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="pf-status">Staða</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => onChange('status', value)}
            >
              <SelectTrigger id="pf-status">
                <SelectValue placeholder="Veldu stöðu" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="pf-size_sqm" className="flex items-center gap-1">
              <Maximize className="w-3 h-3" />
              Stærð (m²)
            </Label>
            <Input
              id="pf-size_sqm"
              type="number"
              min="0"
              value={formData.size_sqm}
              onChange={(e) => onChange('size_sqm', e.target.value)}
              placeholder="85"
            />
          </div>
          <div>
            <Label htmlFor="pf-rooms" className="flex items-center gap-1">
              <DoorOpen className="w-3 h-3" />
              Herbergi
            </Label>
            <Input
              id="pf-rooms"
              type="number"
              min="0"
              value={formData.rooms}
              onChange={(e) => onChange('rooms', e.target.value)}
              placeholder="4"
            />
          </div>
          <div>
            <Label htmlFor="pf-bedrooms" className="flex items-center gap-1">
              <BedDouble className="w-3 h-3" />
              Svefnherb.
            </Label>
            <Input
              id="pf-bedrooms"
              type="number"
              min="0"
              value={formData.bedrooms}
              onChange={(e) => onChange('bedrooms', e.target.value)}
              placeholder="2"
            />
          </div>
          <div>
            <Label htmlFor="pf-bathrooms" className="flex items-center gap-1">
              <Bath className="w-3 h-3" />
              Baðherb.
            </Label>
            <Input
              id="pf-bathrooms"
              type="number"
              min="0"
              value={formData.bathrooms}
              onChange={(e) => onChange('bathrooms', e.target.value)}
              placeholder="1"
            />
          </div>
        </div>
      </div>

      {/* Price section */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <Banknote className="w-4 h-4 text-gray-400" />
          Verð
        </h3>
        <div>
          <Label htmlFor="pf-price">Verð (kr.)</Label>
          <Input
            id="pf-price"
            type="text"
            value={formData.price}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              const formatted = value ? parseInt(value, 10).toLocaleString('is-IS') : '';
              onChange('price', formatted);
            }}
            placeholder="45.000.000"
          />
        </div>
      </div>

      {/* Description section */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900">Lýsing</h3>
        <div>
          <Label htmlFor="pf-description">Lýsing á eign</Label>
          <Textarea
            id="pf-description"
            value={formData.description}
            onChange={(e) => onChange('description', e.target.value)}
            rows={4}
            placeholder="Lýsing á eigninni, sérstökum eiginleikum, staðsetningu o.s.frv."
          />
          <ProofreadingIndicator
            isChecking={proofreader.isChecking}
            hasErrors={proofreader.hasErrors}
            corrections={proofreader.corrections}
            onApplyCorrection={(c) => onChange('description', proofreader.applyCorrection(c))}
            onApplyAll={() => onChange('description', proofreader.applyAll())}
          />
        </div>
      </div>

      {/* Images / AI tools slot — provided by parent */}
      {children && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-gray-400" />
            Myndir
          </h3>
          {children}
        </div>
      )}
    </div>
  );
}
